// DEPENDENCIES
import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useMsal } from "@azure/msal-react";

// MUI COMPONENTS
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";

// MUI ICONS
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LaunchIcon from "@mui/icons-material/Launch";
import TimelapseIcon from "@mui/icons-material/Timelapse";

// API
import { getAPICall, putAPICall } from "../../../config/apiCalls";

// CONSTANTS
import { notApplicable } from "../../Misc/Table/TanStackTableConstants";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// COMPONENTS
import ConfirmRemovePhaseDialog from "../../Specifications/DesignJobs/ConfirmRemovePhaseDialog/ConfirmRemovePhaseDialog";

// Same rule the Design Jobs page uses: these phase types are always design jobs,
// so they can't be toggled off — only other phase types are included manually.
const DESIGN_PHASE_TYPE_IDS = [2, 7];

const formatTimestamp = (value) => {
	if (!value) return notApplicable;
	const date = new Date(value);
	if (isNaN(date.getTime())) return notApplicable;
	return date.toLocaleDateString("en-US", {
		month: "long",
		day: "numeric",
		year: "numeric",
	});
};

// Same download filenames the Spec Book and Scope of Work pages use.
const buildSpecBookFileName = (specBook) => {
	const date = new Date(specBook.created);
	const month = String(date.getUTCMonth() + 1).padStart(2, "0");
	const day = String(date.getUTCDate()).padStart(2, "0");
	return `${String(specBook.title || "Spec_Book").replace(/ /g, "_")}_${month}_${day}_${date.getUTCFullYear()}.pdf`;
};

const buildScopeOfWorkFileName = (propertyName, sowExport) => {
	const ext = sowExport.format_excel ? "xlsx" : "docx";
	const propName = (propertyName || "scope").replace(/[^a-z0-9-_ ]/gi, " ").trim();
	return `SOW ${propName}.${ext}`;
};

// ─── LOADING / ERROR / EMPTY STATES ────────────────────────────────────────────

const renderLoadingNotice = () => (
	<div className="projectViewerLoadingNotice">
		<div className="projectViewerLoadingNoticeIconWrap">
			<TimelapseIcon fontSize="inherit" />
		</div>
		<div className="projectViewerLoadingNoticeContent">
			<div className="projectViewerLoadingNoticeTitle">Loading</div>
			<div className="projectViewerLoadingNoticeMessage">Loading design status...</div>
		</div>
		<div className="projectViewerLoadingNoticeBars">
			<div className="projectViewerLoadingNoticeBar projectViewerShimmer" />
			<div className="projectViewerLoadingNoticeBar projectViewerShimmer short" />
		</div>
	</div>
);

const renderEmptyStateMessage = (message) => (
	<div className="projectViewerEmptyStateMessage">{message}</div>
);

const renderStartedBadge = (isStarted) => (
	<span className={`projectViewerDocStatusBadge ${isStarted ? "started" : "notStarted"}`}>
		{isStarted ? "Started" : "Not Started"}
	</span>
);

// ─── COMPONENT ────────────────────────────────────────────────────────────────

const DesignTab = ({ bqePhaseId, phaseName, hasDesignAccess, refreshCount }) => {
	const { instance, accounts } = useMsal();
	const employeeID = useContext(UserContext);

	const [phase, setPhase] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [loadError, setLoadError] = useState("");
	const [isSaving, setIsSaving] = useState(false);
	const [saveError, setSaveError] = useState("");
	const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
	const [downloadingKey, setDownloadingKey] = useState("");
	const [downloadErrorKey, setDownloadErrorKey] = useState("");

	const fetchDesignStatus = async () => {
		setIsLoading(true);
		setLoadError("");
		try {
			const res = await getAPICall(instance, accounts[0], "/api/jobs/design/status/byBqeId", {
				bqeId: bqePhaseId,
			});
			setPhase(res?.data?.phase || null);
		} catch (err) {
			console.error("[DesignTab] failed to load design status", err);
			setLoadError("Couldn't load this phase's design status.");
		}
		setIsLoading(false);
	};

	useEffect(() => {
		if (!bqePhaseId || !hasDesignAccess) return;
		fetchDesignStatus();
	}, [bqePhaseId, hasDesignAccess, refreshCount]);

	const setIncludeInDesignJobs = async (include) => {
		setIsSaving(true);
		setSaveError("");
		try {
			await putAPICall(instance, accounts[0], "/api/jobs/design/toggleInclude", {
				id: phase.id,
				include: include ? 1 : 0,
				employeeID,
			});
			await fetchDesignStatus();
		} catch (err) {
			console.error("[DesignTab] failed to update design jobs inclusion", err);
			setSaveError(include ? "Couldn't add to Design Jobs." : "Couldn't remove from Design Jobs.");
		}
		setIsSaving(false);
	};

	const handleDesignPhaseToggle = (event) => {
		if (event.target.checked) {
			setIncludeInDesignJobs(true);
		} else {
			setConfirmRemoveOpen(true);
		}
	};

	const handleRemoveConfirmed = () => {
		setConfirmRemoveOpen(false);
		setIncludeInDesignJobs(false);
	};

	const handleDownload = async (key, endpoint, s3Name, title) => {
		setDownloadingKey(key);
		setDownloadErrorKey("");
		try {
			const res = await getAPICall(instance, accounts[0], endpoint, { s3Name, title });
			window.open(res.data, "_blank");
		} catch (err) {
			console.error("[DesignTab] failed to download document", err);
			setDownloadErrorKey(key);
		}
		setDownloadingKey("");
	};

	const isDesignPhaseType = DESIGN_PHASE_TYPE_IDS.includes(phase?.phase_type_id);
	const showToggle = hasDesignAccess && !!phase && !loadError;
	const showDesignDetails = hasDesignAccess && !loadError && !!phase?.is_design_job;

	const renderToolsBody = () => {
		if (!hasDesignAccess) {
			return renderEmptyStateMessage("You don't have access to the design tools.");
		}
		if (isLoading && !phase) {
			return renderLoadingNotice();
		}
		if (loadError) {
			return renderEmptyStateMessage(loadError);
		}
		if (!phase) {
			return renderEmptyStateMessage(
				"This phase hasn't been synced to J2 Tools yet, so it can't be added to Design Jobs."
			);
		}
		if (!phase.is_design_job) {
			return renderEmptyStateMessage(
				"This phase isn't a design job. Turn on Design Phase to use the Spec Book and Scope of Work tools for it."
			);
		}
		return (
			<div className="projectViewerQuickLinksGrid">
				<RouterLink className="projectViewerQuickLinkItem" to={`/design/spec_books/${phase.id}`}>
					<div className="projectViewerIntegrationBadge design">SB</div>
					<div className="projectViewerDesignToolTitle">
						<span className="projectViewerQuickLinkTitle">Spec Book</span>
						{renderStartedBadge(phase.spec_book_started)}
					</div>
					<ChevronRightIcon fontSize="small" />
				</RouterLink>
				<RouterLink className="projectViewerQuickLinkItem" to={`/design/scope_of_work/${phase.id}`}>
					<div className="projectViewerIntegrationBadge design">SW</div>
					<div className="projectViewerDesignToolTitle">
						<span className="projectViewerQuickLinkTitle">Scope of Work</span>
						{renderStartedBadge(phase.scope_of_work_started)}
					</div>
					<ChevronRightIcon fontSize="small" />
				</RouterLink>
			</div>
		);
	};

	// One row per document type; a type with several formats (Scope of Work)
	// stacks one line per generated file in each column.
	const renderDocumentRow = ({ key, label, endpoint, files }) => {
		const generatedFiles = files.filter((file) => file.document);
		return (
			<tr key={key}>
				<td>{label}</td>
				<td>
					{generatedFiles.length === 0 ? (
						<span className="projectViewerMutedText">None generated yet</span>
					) : (
						<div className="projectViewerDesignFileList">
							{generatedFiles.map(({ key: fileKey, document, fileName }) => (
								<span key={fileKey} className="projectViewerDesignDownload">
									<button
										type="button"
										className="projectViewerTableLink projectViewerDesignDocumentLink"
										disabled={downloadingKey === fileKey}
										onClick={() => handleDownload(fileKey, endpoint, document.s3_name, fileName)}
									>
										{downloadingKey === fileKey ? "Preparing…" : fileName}
										<LaunchIcon fontSize="inherit" />
									</button>
									{downloadErrorKey === fileKey && (
										<span className="projectViewerStatusError">Download unavailable — try again.</span>
									)}
								</span>
							))}
						</div>
					)}
				</td>
				<td>
					{generatedFiles.length === 0 ? (
						notApplicable
					) : (
						<div className="projectViewerDesignFileList">
							{generatedFiles.map(({ key: fileKey, document }) => (
								<span key={fileKey}>{formatTimestamp(document.created)}</span>
							))}
						</div>
					)}
				</td>
				<td>
					{generatedFiles.length === 0 ? (
						notApplicable
					) : (
						<div className="projectViewerDesignFileList">
							{generatedFiles.map(({ key: fileKey, document }) => (
								<span key={fileKey}>{document.employee_name || notApplicable}</span>
							))}
						</div>
					)}
				</td>
			</tr>
		);
	};

	const renderGeneratedDocuments = () => (
		<div className="projectViewerPaper">
			<div className="projectViewerPaperHeader">
				<div>
					<h3>Generated Documents</h3>
					<p>Most recent version of each</p>
				</div>
			</div>
			<div className="projectViewerPaperBody projectViewerCompactTableBody">
				<table className="projectViewerTable">
					<thead>
						<tr>
							<th>Document</th>
							<th>File</th>
							<th>Generated</th>
							<th>Generated By</th>
						</tr>
					</thead>
					<tbody>
						{renderDocumentRow({
							key: "specBook",
							label: "Spec Book",
							endpoint: "/api/specifications/specBooks/byS3Name",
							files: [
								{
									key: "specBook",
									document: phase.latest_spec_book,
									fileName: phase.latest_spec_book && buildSpecBookFileName(phase.latest_spec_book),
								},
							],
						})}
						{renderDocumentRow({
							key: "scopeOfWork",
							label: "Scope of Work",
							endpoint: "/api/scopeOfWork/exports/byS3Name",
							files: [
								{
									key: "scopeOfWorkExcel",
									document: phase.latest_scope_of_work_excel,
									fileName:
										phase.latest_scope_of_work_excel &&
										buildScopeOfWorkFileName(phase.property_name, phase.latest_scope_of_work_excel),
								},
								{
									key: "scopeOfWorkWord",
									document: phase.latest_scope_of_work_word,
									fileName:
										phase.latest_scope_of_work_word &&
										buildScopeOfWorkFileName(phase.property_name, phase.latest_scope_of_work_word),
								},
							],
						})}
					</tbody>
				</table>
			</div>
		</div>
	);

	return (
		<div className="projectViewerTabPanel" style={{ gap: 16 }}>
			<div className="projectViewerPaper">
				<div className="projectViewerPaperHeader projectViewerCardHeaderWithAction">
					<div>
						<h3>Design</h3>
					</div>
					{showToggle && (
						<div className="projectViewerDesignToggle">
							{saveError && <span className="projectViewerStatusError">{saveError}</span>}
							<Tooltip
								arrow
								title={isDesignPhaseType ? "This phase's type makes it a design job automatically." : ""}
							>
								<FormControlLabel
									control={
										<Switch
											size="small"
											checked={!!phase.is_design_job}
											disabled={isDesignPhaseType || isSaving || isLoading}
											onChange={handleDesignPhaseToggle}
										/>
									}
									label="Design Phase"
									slotProps={{ typography: { fontSize: 13 } }}
									sx={{ mr: 0 }}
								/>
							</Tooltip>
						</div>
					)}
				</div>
				<div className="projectViewerPaperBody">
					{renderToolsBody()}
				</div>
			</div>
			{showDesignDetails && renderGeneratedDocuments()}
			<ConfirmRemovePhaseDialog
				open={confirmRemoveOpen}
				jobName={phaseName}
				onCancel={() => setConfirmRemoveOpen(false)}
				onConfirm={handleRemoveConfirmed}
			/>
		</div>
	);
};

export default DesignTab;
